import { useQueryInvalidation } from "@/lib/queryInvalidation";
import { queryKeys } from "@/lib/queryKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createCurrency,
  createExchangeRateSupplier,
  createNewQuarterExchangeRates,
  getCurrencies,
  getExchangeRatesByYear,
  getExchangeRateSuppliers,
  updateCurrency,
  updateExchangeRates,
  updateExchangeRateSupplier,
  type createQuarterBody,
  type currencyBody,
  type exchangeRateSupplier,
  type updateExchangeRateBody,
} from "@/api/exchangeRateService";
import { CACHE_TIME } from "@/lib/cacheConfig";

export const useGetExchangeRatesByYear = (year: number) => {
  return useQuery({
    queryKey: queryKeys.exchangeRate.list(year),
    queryFn: () => getExchangeRatesByYear(year),
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useUpdateExchangeRates = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.exchangeRate.update(),
    mutationFn: (data: updateExchangeRateBody) => updateExchangeRates(data),
    onSuccess: () => {
      queryInvalidation.invalidateExchangeRates();
    },
  });
};

export const useCreateQuarterExchangeRates = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.exchangeRate.createQuarter(),
    mutationFn: (params: createQuarterBody) =>
      createNewQuarterExchangeRates(params),
    onSuccess: () => {
      queryInvalidation.invalidateExchangeRates();
    },
  });
};

export const useGetExchangeRateSuppliers = () => {
  return useQuery({
    queryKey: queryKeys.exchangeRate.suppliers(),
    queryFn: () => getExchangeRateSuppliers(),
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useCreateExchangeRateSupplier = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.exchangeRate.createSupplier(),
    mutationFn: (data: exchangeRateSupplier) =>
      createExchangeRateSupplier(data),
    onSuccess: () => {
      queryInvalidation.invalidateExchangeRateSuppliers();
    },
  });
};

export const useUpdateExchangeRateSupplier = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.exchangeRate.updateSupplier(),
    mutationFn: (data: exchangeRateSupplier) =>
      updateExchangeRateSupplier(data),
    onSuccess: () => {
      queryInvalidation.invalidateExchangeRateSuppliers();
    },
  });
};

export const useGetCurrencies = () => {
  return useQuery({
    queryKey: queryKeys.exchangeRate.currencies(),
    queryFn: () => getCurrencies(),
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useCreateCurrency = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.exchangeRate.createCurrency(),
    mutationFn: (data: currencyBody) => createCurrency(data),
    onSuccess: () => {
      queryInvalidation.invalidateExchangeRateCurrencies();
    },
  });
};

export const useUpdateCurrency = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.exchangeRate.updateCurrency(),
    mutationFn: (data: currencyBody) => updateCurrency(data),
    onSuccess: () => {
      queryInvalidation.invalidateExchangeRateCurrencies();
    },
  });
};
